import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import swal from "sweetalert";
import axios from "axios";
import Loader from "../CustomCommons/Loader";
import RequiredLabel from "../CustomCommons/RequiredLabel";
import { Form, InputGroup } from "react-bootstrap";
import { BASE_URL } from "../Const/Url";
import { showToast } from "../../utils/ToastHelper";

const Edit_Theme = () => {
  const { id } = useParams();
  const history = useHistory();

  const [themeName, setThemeName] = useState("");
  const [themeDescription, setThemeDescription] = useState("");
  const [order, setOrder] = useState("");
  const [mediaType, setMediaType] = useState(0); // 0 = image, 1 = video
  const [singleImg, setSingleImage] = useState(null);
  const [videoId, setVideoId] = useState(null);
  const [videoName, setVideoName] = useState("");
  const [videoPreview, setVideoPreview] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isShowWebsite, setIsShowWebsite] = useState(true);

  // Fetch theme details
  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const url = `${BASE_URL}api/v1/inventory/inventory/themes/${id}/`;
        const res = await axios.get(url);
        const data = res?.data?.data || res?.data; // support both shapes
        setThemeName(data?.name || "");
        setThemeDescription(data?.description || "");
        setIsShowWebsite(Boolean(data?.is_show_website ?? true));
        setOrder(data?.order ?? "");

        // Check if video exists, if yes set mediaType to 1, else 0
        const existingVideoId = data?.video ?? data?.video_id ?? null;
        if (existingVideoId) {
          setMediaType(1);
          setVideoId(existingVideoId);
          if (data?.video_url) {
            setVideoPreview(data.video_url);
          }
          if (data?.video_details?.document) {
            setVideoName(data.video_details.document);
          }
        } else {
          setMediaType(0);
          // Try to resolve existing thumb preview and id
          const existingThumbId = data?.thumb ?? data?.thumb_id ?? null;
          const existingThumbUrl =
            data?.thumb_details?.doc_url || data?.thumb_url || null;
          if (existingThumbId || existingThumbUrl) {
            setSingleImage({ id: existingThumbId, url: existingThumbUrl });
          }
        }
      } catch (error) {
        showToast("error", "Failed to load theme details");
      } finally {
        setLoading(false);
      }
    };
    fetchTheme();
  }, [id]);

  const uploadDocument = (file, onSuccess, docType = 0) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("document", file);
    formData.append("doc_type", docType);

    setUploading(true);
    axios
      .post(`${BASE_URL}api/v1/auth/documents/upload/`, formData)
      .then((res) => {
        if (res?.data?.status) {
          onSuccess(res?.data?.data);
          showToast("success", "File uploaded successfully");
        } else {
          showToast("error", "Failed to upload file");
        }
      })
      .catch((err) => {
        const message =
          err?.response?.data?.errors?.document?.[0] ||
          err?.response?.data?.message ||
          "Upload failed";
        showToast("error", message);
      })
      .finally(() => {
        setUploading(false);
      });
  };

  // Upload single image
  const uploadSingle = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    uploadDocument(
      file,
      (data) => {
        setSingleImage({ id: data.id, url: data?.doc_url });
      },
      0
    );
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    uploadDocument(
      file,
      (data) => {
        setVideoId(data.id);
        setVideoName(file.name);
        const reader = new FileReader();
        reader.onload = () => setVideoPreview(reader.result);
        reader.readAsDataURL(file);
      },
      1
    );
  };

  const resetMedia = () => {
    setSingleImage(null);
    setVideoId(null);
    setVideoName("");
    setVideoPreview("");
  };

  const removeSingleImg = () => {
    swal({
      title: "Are you sure?",
      text: "Once Removed, you will not be able to recover this file!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        swal("Poof! Your file has been Removed!", { icon: "success" });
        setSingleImage(null);
      } else {
        swal("Your file is safe!");
      }
    });
  };

  const removeVideo = () => {
    swal({
      title: "Are you sure?",
      text: "Once Removed, you will not be able to recover this file!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        swal("Poof! Your file has been Removed!", { icon: "success" });
        setVideoId(null);
        setVideoName("");
        setVideoPreview("");
      } else {
        swal("Your file is safe!");
      }
    });
  };

  const handleSubmit = () => {
    // Validation
    if (!themeName.trim()) {
      showToast("error", "Theme name is required");
      return;
    }

    if (!order && order !== 0) {
      showToast("error", "Order is required");
      return;
    }

    const url = `${BASE_URL}api/v1/inventory/inventory/themes/${id}/`;
    const payload = {
      name: themeName,
      description: themeDescription,
      is_show_website: Boolean(isShowWebsite),
      order: Number(order),
    };

    // Only send thumb or video if they exist
    if (Number(mediaType) === 0 && singleImg?.id) {
      payload.thumb = singleImg.id;
    }
    if (Number(mediaType) === 1 && videoId) {
      payload.video = videoId;
    }

    setLoading(true);
    axios
      .patch(url, payload)
      .then((res) => {
        if (res.data.status) {
          showToast("success", "Theme updated successfully.");
          history.push("/theme");
        }
      })
      .catch((err) => {
        const message = err?.response?.data?.message || "An error occurred";
        const errorMsg = err?.response?.data?.errors;
        if (errorMsg) {
          for (let key in errorMsg) {
            showToast("error", `${key} : ${errorMsg[key][0]}`);
          }
        } else {
          showToast("error", message);
        }
      })
      .finally(() => setLoading(false));
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="categorey-parent new-categorey-parent an">
      <div className="bg-white add-div">
        <div
          className="d-flex align-items-center"
          style={{ padding: "20px 35px ", border: "0.5px solid #E0E0E0" }}
        >
          <h3 style={{ fontSize: "23px", marginTop: "6px" }}>Edit Theme</h3>
        </div>
        <div
          style={{
            padding: "20px 35px ",
          }}
          className="row"
        >
          <div className="col-12 col-md-12">
            <RequiredLabel text={"Theme Name"} />
            <InputGroup className="mb-3" style={{ height: "47px" }}>
              <Form.Control
                aria-label="Theme Name"
                aria-describedby="basic-addon1"
                style={{ backgroundColor: "#FAFAFA" }}
                name="name"
                value={themeName}
                onChange={(e) => setThemeName(e.target.value)}
                placeholder="Enter theme name"
              />
            </InputGroup>
          </div>

          <div className="col-12 col-md-12">
            <label
              style={{ color: "#4F4F4F", fontSize: "14px", fontWeight: "500" }}
            >
              Description
            </label>
            <Form.Control
              as="textarea"
              rows={4}
              style={{ backgroundColor: "#FAFAFA" }}
              name="description"
              value={themeDescription}
              onChange={(e) => setThemeDescription(e.target.value)}
              placeholder="Enter theme description (optional)"
            />
          </div>

          <div className="col-12 col-md-6">
            <RequiredLabel text="Order" />
            <InputGroup className="mb-3" style={{ height: "47px" }}>
              <Form.Control
                aria-label="Order"
                aria-describedby="order-input"
                style={{ backgroundColor: "#FAFAFA" }}
                value={order}
                type="number"
                min={0}
                onChange={(e) => setOrder(e.target.value)}
                placeholder="Enter display order"
              />
            </InputGroup>
          </div>

          <div className="col-12 col-md-6 mb-3">
            <RequiredLabel text="Media Type" />
            <Form.Select
              value={mediaType}
              onChange={(e) => {
                const value = Number(e.target.value);
                setMediaType(value);
                resetMedia();
              }}
              style={{ height: "47px", backgroundColor: "#FAFAFA" }}
            >
              <option value={0}>Image</option>
              <option value={1}>Video</option>
            </Form.Select>
          </div>

          <div className="col-12 mt-3">
            {Number(mediaType) === 0 ? (
              <>
                <label
                  style={{
                    color: "#4F4F4F",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Add Image (Optional)
                </label>
                <p style={{ marginTop: "10px", color: "red" }}>
                  NB: Upload image in (width-260px, height-200px) for Theme.
                </p>
                <p style={{ color: "#8E8E93" }}>
                  ( Select your file & upload )
                </p>
                <div
                  className="w-100 browse-main d-flex align-items-center"
                  style={{
                    height: "55px",
                    border: "1px solid #E0E0E0",
                    borderRadius: "7px",
                    overflowX: "hidden",
                  }}
                >
                  <p className="my-2 ms-2" style={{ color: "#4F4F4F" }}>
                    {singleImg ? "File Chosen" : "No File Chosen"}
                  </p>

                  <div className="file-up">
                    <input
                      type="file"
                      onChange={uploadSingle}
                      accept="image/*"
                    />
                    <p>Browse</p>
                  </div>
                </div>
                {uploading && (
                  <p style={{ color: "#8E8E93", fontSize: 12, marginTop: 6 }}>
                    Please wait, image is uploading...
                  </p>
                )}
                {singleImg && (
                  <div className="my-2">
                    <img height={90} width={90} src={singleImg?.url} alt="" />
                    <br />
                    <button
                      onClick={removeSingleImg}
                      className="remove-btn btn btn-small mt-1 rounded"
                      style={{ border: "1px solid gray !important" }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <label
                  style={{
                    color: "#4F4F4F",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Add Video (Optional)
                </label>
                <p style={{ color: "#8E8E93" }}>
                  ( Select your file & upload )
                </p>
                <div
                  className="w-100 browse-main d-flex align-items-center"
                  style={{
                    height: "55px",
                    border: "1px solid #E0E0E0",
                    borderRadius: "7px",
                    overflowX: "hidden",
                  }}
                >
                  <p className="my-2 ms-2" style={{ color: "#4F4F4F" }}>
                    {videoName ? "File Chosen" : "No File Chosen"}
                  </p>

                  <div className="file-up">
                    <input
                      type="file"
                      onChange={handleVideoUpload}
                      accept="video/*"
                    />
                    <p>Browse</p>
                  </div>
                </div>
                {uploading && (
                  <p style={{ color: "#8E8E93", fontSize: 12, marginTop: 6 }}>
                    Please wait, video is uploading...
                  </p>
                )}
                {videoPreview && (
                  <div className="my-2">
                    <video
                      height={120}
                      width={160}
                      src={videoPreview}
                      controls
                    />
                    <br />
                    <button
                      onClick={removeVideo}
                      className="remove-btn btn btn-small mt-1 rounded"
                      style={{ border: "1px solid gray !important" }}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="col-12 col-md-6 mt-3">
            <RequiredLabel text="Show on Website" />
            <div
              className="d-flex justify-content-between align-items-center py-3 px-3 mb-3"
              style={{ backgroundColor: "#F2F2F7", borderRadius: "8px" }}
            >
              <span>{isShowWebsite ? "Active" : "Disable"}</span>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={isShowWebsite}
                  onChange={(e) => setIsShowWebsite(e.target.checked)}
                />
              </div>
            </div>
          </div>

          <div className="mt-2 mt-md-4" style={{ marginLeft: "-10px" }}>
            <button
              className="save-btn me-2 mt-2"
              style={{ background: "#000", color: "white" }}
              onClick={handleSubmit}
            >
              Save
            </button>
            <button
              className="save-btn mt-2"
              style={{ border: ".4px solid #E0E0E0 !important" }}
              onClick={() => history.push("/theme")}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Edit_Theme;
